import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ChargingPoint } from '../../../../assets/icons/dashboard/charging-point.svg';
import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import { updateDeviceFilters } from '../../../../redux/slices/Partners/DeviceManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import dashboard from '../../../../utils/api/partner/dashboard';

interface DeviceStatusProperties {
  status: 'Online' | 'Offline' | string;
  count: number;
}

/**
 * Device status component
 * @returns {any} The result
 */
export default function DeviceStatus() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [deviceStatusCount, setDeviceStatusCount] = useState<{ online: number; offline: number }>({
    online: 0,
    offline: 0,
  });

  /**
   * Function to fetch connector status report
   */
  const fetchConnectorStatusReport = () => {
    setIsLoading(true);
    dashboard
      .getDeviceStatusReport()
      .then((response: { data: DeviceStatusProperties[] }) => {
        const result = response.data;

        const statusMap: { online: number; offline: number } = {
          online: 0,
          offline: 0,
        };

        result.forEach((item) => {
          const key = item.status.toLowerCase() as 'online' | 'offline';
          if (key === 'online' || key === 'offline') {
            statusMap[key] = item.count;
          }
        });

        setDeviceStatusCount(statusMap);
        setIsLoading(false);
      })
      .catch((error: any) => {
        setIsLoading(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle redirect device management page with filter
   * @param {string} key - The key
   * @returns {void}
   */
  const handleClickPieChartCell = (key: string): void => {
    dispatch(
      updateDeviceFilters({
        key: 'deviceStatus',
        value: [key],
        type: 'restAndAdd',
      }),
    );
    navigate({
      pathname: '/ev-admin/operations/chargers',
      search: `?${new URLSearchParams({
        deviceStatus: key,
      })}`,
    });
  };

  useEffect(() => {
    fetchConnectorStatusReport();
  }, []);

  return (
    <div className="dashboard-device-status-wrap-partner">
      <div className="dashboard-device-status-header">
        <div className="header-title">
          <h6>{t('deviceStatus')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/operations/chargers')}>
            <NavigateArrow />
          </span>
        </div>
      </div>
      {isLoading ? (
        <center className="device-status-loader">
          <BatteryLoader />
        </center>
      ) : (
        <div className="dashboard-device-status-container">
          <div className="device-status-top">
            <div
              className="outer-circle go-green"
              role="presentation"
              onClick={() => handleClickPieChartCell('online')}>
              <div className="inner-circle">{deviceStatusCount?.online}</div>
            </div>
            <div
              className="outer-circle out-of-service"
              role="presentation"
              onClick={() => handleClickPieChartCell('offline')}>
              <div className="inner-circle">{deviceStatusCount?.offline}</div>
            </div>
            <div className="absolute-container">
              <svg
                width="213"
                height="28"
                viewBox="0 0 213 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none">
                <path
                  d="M1.22266 15.9421C9.76059 10.3572 33.8465 -0.315986 61.8869 1.66973C96.9373 4.15187 198.044 37.0397 212.199 23.3879"
                  stroke="url(#paint0_linear_49_10663)"
                  strokeWidth={1.26333}
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_49_10663"
                    x1="1.22266"
                    y1="14.0601"
                    x2="212.199"
                    y2="14.0601"
                    gradientUnits="userSpaceOnUse">
                    <stop stopColor="hsl(from var(--primary-color) h s l)" />
                    <stop
                      offset="1"
                      stopColor="hsl(from var(--primary-color) calc(h - 42) calc(s - 59) calc(l + 27))"
                    />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
          <div className="device-status-bottom">
            <div className="outer-circle go-green">
              <div className="inner-circle">
                <ChargingPoint />
                <p>{t('greenGo')}</p>
              </div>
            </div>
            <div className="outer-circle out-of-service">
              <div className="inner-circle">
                <ChargingPoint />
                <p>{t('outOfService')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
